import React, { useEffect, useState } from "react";
import {
    Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
    Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { RefreshCw } from "lucide-react";
import { adminApi } from "@/lib/api";

const SENTIMENT_COLORS = {
    Positive: "#536F55",
    Neutral: "#94A892",
    Negative: "#A55691",
    Unspecified: "#D6D3D1",
};

export default function Sentiment() {
    const [data, setData] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = () => adminApi.get("/api/admin/analytics/sentiment").then((r) => setData(r.data));
    useEffect(() => { load(); }, []);

    const refresh = async () => {
        setRefreshing(true);
        try {
            await adminApi.post("/api/admin/analytics/refresh", null, { params: { limit: 100 } });
            await load();
        } finally { setRefreshing(false); }
    };

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    return (
        <div className="p-10 max-w-7xl">
            <div className="mb-8 flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Analytics</div>
                    <h1 className="font-heading text-5xl text-orchid-700">Sentiment</h1>
                    <p className="text-stone-600 mt-2">
                        How guests are feeling — {data.total} conversations classified.
                    </p>
                </div>
                <button onClick={refresh} disabled={refreshing} data-testid="sentiment-refresh"
                    className="flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 disabled:opacity-60">
                    <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
                    {refreshing ? "Classifying…" : "Refresh"}
                </button>
            </div>

            {data.total === 0 ? (
                <div className="bg-white border border-stone-200 rounded-lg p-12 text-center text-stone-500">
                    No classified conversations yet. Click <strong>Refresh</strong>.
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                        {data.by_sentiment.map((s) => (
                            <div key={s.sentiment} data-testid={`sent-stat-${s.sentiment}`}
                                className="bg-white border border-stone-200 rounded-lg p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{s.sentiment}</div>
                                    <span className="w-3 h-3 rounded-full" style={{ background: SENTIMENT_COLORS[s.sentiment] || "#888" }} />
                                </div>
                                <div className="font-heading text-5xl text-orchid-700">{s.count}</div>
                                <div className="text-xs text-stone-500 mt-1">{s.pct}% of total</div>
                            </div>
                        )).slice(0, 3)}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
                        <div className="bg-white border border-stone-200 rounded-lg p-6">
                            <h3 className="font-heading text-2xl text-orchid-700 mb-5">Distribution</h3>
                            <div className="h-64 min-h-64" data-testid="chart-sentiment-pie">
                                <ResponsiveContainer width="100%" height={256}>
                                    <PieChart>
                                        <Pie data={data.by_sentiment} dataKey="count" nameKey="sentiment" outerRadius={80} innerRadius={45}>
                                            {data.by_sentiment.map((e) => (
                                                <Cell key={e.sentiment} fill={SENTIMENT_COLORS[e.sentiment] || "#888"} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="bg-white border border-stone-200 rounded-lg p-6">
                            <h3 className="font-heading text-2xl text-orchid-700 mb-5">Volume</h3>
                            <div className="h-64 min-h-64" data-testid="chart-sentiment-bar">
                                <ResponsiveContainer width="100%" height={256}>
                                    <BarChart data={data.by_sentiment}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                                        <XAxis dataKey="sentiment" tick={{ fill: "#78716c", fontSize: 11 }} />
                                        <YAxis tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                            {data.by_sentiment.map((e) => (
                                                <Cell key={e.sentiment} fill={SENTIMENT_COLORS[e.sentiment] || "#888"} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {data.series?.length > 0 && (
                        <div className="bg-white border border-stone-200 rounded-lg p-6">
                            <h3 className="font-heading text-2xl text-orchid-700 mb-5">Sentiment over time</h3>
                            <div className="h-64 min-h-64" data-testid="chart-sentiment-series">
                                <ResponsiveContainer width="100%" height={256}>
                                    <LineChart data={data.series}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fill: "#78716c", fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                                        <YAxis tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        {data.sentiments.map((s) => (
                                            <Line key={s} type="monotone" dataKey={s} stroke={SENTIMENT_COLORS[s] || "#888"} strokeWidth={2} dot={false} />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
